<template>
  <div class="content mb-6 mt-8">
    <div class="password-tip">
      <p><i>*</i>请输入 6 位数字授权密码：</p>
      <div class="eye-icon" @click="togglePassword">
        <EyeOutlined v-if="showPassword" />
        <EyeInvisibleOutlined v-else />
      </div>
    </div>
    <input type="password" style="display: none" autocomplete="new-password" />
    <div class="otp-container">
      <input
        v-for="(_, index) in 6"
        :key="index"
        :ref="(el) => (inputRefs[index] = el ? el as HTMLInputElement : null)"
        v-model="codeArray[index]"
        :type="showPassword ? 'text' : 'password'"
        maxlength="1"
        class="otp-input"
        :style="getFocusStyle()"
        @input="handleInput(index)"
        @keydown="handleKeydown($event, index)"
        @paste="handlePaste"
        autocomplete="off"
        autofocus
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, defineEmits, defineProps, onMounted, nextTick } from "vue";
import { EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons-vue";
const props = defineProps({
  color: {
    type: String,
    default: "#4749ad", // 默认主题色
  },
});
const emit = defineEmits(["update:code"]);

const showPassword = ref(false);
const inputRefs = ref<(HTMLInputElement | null)[]>([]);
const codeArray = ref<string[]>(Array(6).fill(""));

const togglePassword = () => {
  showPassword.value = !showPassword.value;
};

onMounted(() => {
  // 自动聚焦到第一个输入框
  nextTick(() => {
    const firstInput = inputRefs.value[0];
    if (firstInput) {
      firstInput.focus();
    }
  });
});

const getFocusStyle = () => {
  return {
    "--focus-border-color": props.color,
  };
};

const handleInput = (index: number) => {
  // 确保输入的是数字
  codeArray.value[index] = codeArray.value[index].replace(/[^0-9]/g, "");

  // 自动跳到下一个输入框
  if (codeArray.value[index] && index < 5) {
    inputRefs.value[index + 1]?.focus();
  }

  emit("update:code", codeArray.value);
};

const handleKeydown = (event: KeyboardEvent, index: number) => {
  if (event.key === "Backspace" && !codeArray.value[index] && index > 0) {
    inputRefs.value[index - 1]?.focus();
  }
};

const handlePaste = (event: ClipboardEvent) => {
  event.preventDefault();
  const pastedText = event.clipboardData?.getData("text") || "";
  const numbers = pastedText
    .replace(/[^0-9]/g, "")
    .split("")
    .slice(0, 6);

  numbers.forEach((num, index) => {
    if (index < 6) {
      codeArray.value[index] = num;
    }
  });

  // 聚焦到最后一个填充的输入框的下一个，或第一个空输入框
  const lastFilledIndex = codeArray.value.findLastIndex((val) => val !== "");
  const focusIndex = Math.min(lastFilledIndex + 1, 5);
  inputRefs.value[focusIndex]?.focus();

  emit("update:code", codeArray.value);
};
</script>

<style scoped>
.content {
  margin-top: 20px;
}

.otp-container {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 6px;
}

.otp-input {
  width: 40px;
  height: 40px;
  padding: 8px;
  font-size: 30px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  text-align: center;
  outline: none;
  transition: all 0.3s;
  border-radius: 4px;
}

input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 80px white inset !important;
  -webkit-text-fill-color: #333 !important;
}

.otp-input:focus {
  border-color: var(--focus-border-color, #4749ad);
}

.otp-input:nth-of-type(3) {
  margin-right: 32px;
}

.password-tip {
  display: flex;
  font-size: 14px;
  justify-content: space-between;
  color: rgba(0, 0, 0, 0.55);
}

.password-tip i {
  color: red;
  font-style: normal;
}

.password-tip .eye-icon {
  color: #c0c2c9;
  cursor: pointer;
}
</style>
